import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Chip, Grid, IconButton, Switch, TextField, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DataTable from '../../../sharedModules/dataTable';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { useCorporateLocation } from '../../../sharedModules/utils/useCorporateLocation';
import { formatDollar } from '../../clients/utils/format';
import { Statement } from '../model';
import { statementsQueryKey, useStatements } from '../hooks/useStatements';
import { useUpdateStatementAttachInvoices } from '../hooks/useUpdateStatementAttachInvoices';
import { useStatementConfig } from '../config/hooks/useStatementConfig';
import StatementPdfsModal from '../components/StatementPdfsModal';
import CollectionClientNotesModal from '../../clients/components/CollectionClientNotesModal';
import { lastContactColumn, ROW_CLICK_TAG } from '../../shared/lastContactColumn';

interface ModalState {
  open: boolean;
  acctNo: string;
  company: string;
}

interface NotesModalState {
  open: boolean;
  clientId: number;
  company: string;
}

const buildColumns = (
  onPdfsClick: (row: Statement) => void,
  onNotesClick: (row: Statement) => void,
  onViewClick: (row: Statement) => void,
  globalAttachInvoices: boolean,
  onToggleAttach: (row: Statement, checked: boolean) => void,
  isTogglingAttach: boolean,
) => [
  {
    name: '',
    width: '50px',
    center: true,
    cell: (row: Statement) => (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onViewClick(row);
        }}
        title="View statement details"
        aria-label={`View details for ${row.company}`}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
    ),
  },
  {
    name: 'Company',
    selector: (row: Statement) => row.company,
    sortable: true,
    grow: 2,
    minWidth: '360px',
    cell: (row: Statement) => (
      <Typography
        variant="body2"
        data-tag={ROW_CLICK_TAG}
        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {row.company}
      </Typography>
    ),
  },
  {
    name: 'Emailable',
    selector: (row: Statement) => row.emailable_status,
    sortable: true,
    cell: (row: Statement) => {
      if (row.emailable_status === 'no_email') {
        return <Chip label="No Email" color="error" size="small" data-tag={ROW_CLICK_TAG} />;
      }
      return <Chip label="Yes" color="success" size="small" data-tag={ROW_CLICK_TAG} />;
    },
    width: '95px',
  },
  {
    name: 'Total Invoiced',
    selector: (row: Statement) => row.total_invoiced,
    cell: (row: Statement) => (
      <Typography variant="body2" data-tag={ROW_CLICK_TAG}>
        {formatDollar(row.total_invoiced)}
      </Typography>
    ),
    sortable: true,
    width: '140px',
  },
  {
    name: 'Total Balance',
    selector: (row: Statement) => row.total_balance,
    cell: (row: Statement) => (
      <Typography
        variant="body2"
        data-tag={ROW_CLICK_TAG}
        sx={{
          color: row.total_balance > 0 ? 'error.main' : row.total_balance < 0 ? 'info.main' : 'text.primary',
        }}
      >
        {formatDollar(row.total_balance)}
      </Typography>
    ),
    sortable: true,
    width: '140px',
  },
  {
    name: 'Invoices',
    // Sort by missing files (total - matched), so descending puts clients with the most missing invoice PDFs on top.
    selector: (row: Statement) => row.invoice_count - row.matched_pdf_count,
    sortable: true,
    center: true,
    width: '120px',
    cell: (row: Statement) => {
      if (row.invoice_count === 0) {
        return (
          <Typography variant="body2" data-tag={ROW_CLICK_TAG}>
            0
          </Typography>
        );
      }
      if (!row.collections_client_id) {
        return (
          <Typography variant="body2" data-tag={ROW_CLICK_TAG}>
            {`${row.matched_pdf_count} / ${row.invoice_count}`}
          </Typography>
        );
      }
      const allMatched = row.matched_pdf_count === row.invoice_count;
      return (
        <Chip
          icon={<PictureAsPdfIcon />}
          label={`${row.matched_pdf_count} / ${row.invoice_count}`}
          size="small"
          color={allMatched ? 'primary' : 'error'}
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            onPdfsClick(row);
          }}
          sx={{ cursor: 'pointer', px: 1, py: 0.5, height: 'auto' }}
          aria-label={`${row.matched_pdf_count} of ${row.invoice_count} invoices have PDFs for ${row.company}`}
        />
      );
    },
  },
  {
    name: 'Invoice PDFs',
    center: true,
    width: '120px',
    cell: (row: Statement) => {
      const effectiveChecked = globalAttachInvoices && row.attach_invoices;
      const disabled = isTogglingAttach || !row.collections_client_id || !globalAttachInvoices;
      const tip = !globalAttachInvoices
        ? 'Turned off because the global invoice-attachment setting is off for this location.'
        : '';

      // Tooltip needs a non-disabled wrapper to receive hover events over a disabled Switch.
      return (
        <Tooltip title={tip} disableHoverListener={!tip}>
          <span data-tag={ROW_CLICK_TAG}>
            <Switch
              checked={effectiveChecked}
              onChange={(e) => onToggleAttach(row, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              color="primary"
              size="small"
              disabled={disabled}
              inputProps={{
                'aria-label': `Invoice attachments for ${row.company}`,
                'aria-disabled': disabled,
              }}
            />
          </span>
        </Tooltip>
      );
    },
  },
  {
    name: 'Notes',
    selector: (row: Statement) => row.notes_count,
    sortable: true,
    center: true,
    cell: (row: Statement) => (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onNotesClick(row);
        }}
        title="View notes"
        aria-label={`${row.notes_count} notes for ${row.company}`}
      >
        <Badge
          badgeContent={row.notes_count}
          color="primary"
          max={99}
          showZero
          sx={{
            '& .MuiBadge-badge': {
              minWidth: 22,
              height: 20,
              padding: '0 6px',
              fontSize: '0.75rem',
              lineHeight: '20px',
              whiteSpace: 'nowrap',
            },
          }}
        >
          <ChatBubbleOutlineIcon fontSize="small" />
        </Badge>
      </IconButton>
    ),
    width: '100px',
  },
  lastContactColumn,
];

const StatementListPage: React.FC = () => {
  const history = useHistory();
  const location = useCorporateLocation('collections');
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useStatements(location ?? '', !!location);
  const { data: configData } = useStatementConfig(location ?? '', !!location);
  const globalAttachInvoices = configData?.data?.attach_invoices ?? true;
  const { updateStatementAttachInvoices, isLoading: isTogglingAttach } = useUpdateStatementAttachInvoices();
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);

  const [statements, setStatements] = useState<Statement[]>([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [modal, setModal] = useState<ModalState>({ open: false, acctNo: '', company: '' });
  const [notesModal, setNotesModal] = useState<NotesModalState>({ open: false, clientId: 0, company: '' });

  useEffect(() => {
    if (data?.data) setStatements(data.data);
  }, [data]);

  const filtered = useMemo(() => {
    const q = companyFilter.trim().toLowerCase();
    return q ? statements.filter((row) => (row.company ?? '').toLowerCase().includes(q)) : statements;
  }, [statements, companyFilter]);

  if (!location) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <PageHeader mainTitle="Statements - Clients" titleIcon={<DescriptionIcon />} />
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Alert severity="info">No Collections location is assigned to this account.</Alert>
        </Grid>
      </Grid>
    );
  }

  const handleClearFilters = () => setCompanyFilter('');

  const handleToggleAttach = async (row: Statement, attach: boolean) => {
    if (!row.collections_client_id) {
      showErrorSnackBar?.('Statement not available for update.');
      return;
    }

    const previous = row.attach_invoices;
    setStatements((prev) => prev.map((s) => (s.acct_no === row.acct_no ? { ...s, attach_invoices: attach } : s)));
    try {
      await updateStatementAttachInvoices({ acctNo: row.acct_no, location, attachInvoices: attach });
      showSuccessSnackBar?.(`Invoice attachments ${attach ? 'enabled' : 'disabled'} for ${row.company}.`);
    } catch (error: any) {
      setStatements((prev) => prev.map((s) => (s.acct_no === row.acct_no ? { ...s, attach_invoices: previous } : s)));
      const serverMessage = error?.response?.data?.errors?.[0] || error?.response?.data?.error;
      showErrorSnackBar?.(serverMessage || 'Failed to update invoice attachments.');
    }
  };

  const handlePdfsClick = (row: Statement) => {
    setModal({ open: true, acctNo: row.acct_no, company: row.company });
  };

  const handleNotesClick = (row: Statement) => {
    if (!row.collections_client_id) return;
    setNotesModal({ open: true, clientId: row.collections_client_id, company: row.company });
  };

  const handleViewStatement = (row: Statement) => {
    history.push(`/collections/statements/${encodeURIComponent(row.acct_no)}`);
  };

  const handlePdfChanged = () => {
    void queryClient.invalidateQueries({ queryKey: statementsQueryKey(location) });
  };

  const tableData = isError ? [] : filtered;

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Statements - Clients" titleIcon={<DescriptionIcon />} />
      </Grid>
      {isError && (
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Alert severity="error">Failed to load statements.</Alert>
        </Grid>
      )}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={4}>
            <TextField
              label="Company Name"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md="auto">
            <Button variant="outlined" size="small" onClick={handleClearFilters} sx={{ mb: 0.25 }}>
              Clear filters
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ mt: 2.5 }}>
        <Card>
          <DataTable
            columns={buildColumns(
              handlePdfsClick,
              handleNotesClick,
              handleViewStatement,
              globalAttachInvoices,
              handleToggleAttach,
              isTogglingAttach,
            )}
            data={tableData}
            progressPending={isLoading}
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25, 50, 100]}
            defaultSortField="total_balance"
            defaultSortAsc={false}
            highlightOnHover
            pointerOnHover
            striped
            noHeader
            onRowClicked={handleViewStatement}
          />
        </Card>
      </Grid>
      <StatementPdfsModal
        open={modal.open}
        onClose={() => setModal({ open: false, acctNo: '', company: '' })}
        acctNo={modal.acctNo}
        company={modal.company}
        location={location}
        onPdfChanged={handlePdfChanged}
      />
      <CollectionClientNotesModal
        open={notesModal.open}
        onClose={() => setNotesModal({ open: false, clientId: 0, company: '' })}
        clientId={notesModal.clientId}
        company={notesModal.company}
        onNotesChanged={() => {
          void queryClient.invalidateQueries({ queryKey: statementsQueryKey(location) });
        }}
      />
    </Grid>
  );
};

export default StatementListPage;
